<template>
  <div class="navbar-start">
    <!-- Mega menu item -->
    <MegaMenu
      heading="Products"
      :content="equipment"
      @navigate="handleNavigationClick"
    />

    <div
      class="navbar-item has-dropdown"
      @click.stop="itemClicked"
      @mouseenter.stop="itemHoverOn"
      @mouseleave.stop="itemHoverOff"
    >
      <a class="navbar-link is-hidden-touch">Services</a>
      <div
        class="navbar-dropdown is-right border-t border-slate-200 bg-white md:mx-auto md:max-w-2xl md:rounded-b md:border-b-2 md:shadow-md"
      >
        <div class="navbar-item no-padding-top no-padding-bottom py-3 md:px-4">
          <p class="is-size-6-desktop u-no-marginpx-6 md:px-2">
            <strong class="text-xs font-semibold text-brand-primary-600 uppercase md:mb-1"
              >Services</strong
            >
          </p>
        </div>
        <div class="grid py-3 md:px-4">
          <router-link
            v-for="service in services.services.items"
            :key="service.url"
            class="navbar-item dropdown-item-icon flex-mobile has-space-between flex items-center px-6 py-1 pr-4 text-base transition-all hover:bg-[#f4f4fa] hover:text-brand-primary md:px-2 text-base md:text-base border-b border-solid border-slate-200 md:border-none last:border-b-0"
            :to="service.url"
            exact-active-class="text-brand-primary-600 bg-slate-100"
            @click="handleNavigationClick"
          >
            <p>{{ service.text }}</p>
            <span class="icon match-line ml-3">
              <Icon :icon="service.icon" />
            </span>
          </router-link>
        </div>
      </div>
    </div>

    <router-link
      class="navbar-item flex-mobile has-space-between is-hidden-touch py-6"
      to="/customer-support"
      active-class="is-active"
      @click="handleNavigationClick"
    >
      <span>Support</span>
      <span class="icon match-line is-hidden-desktop">
        <Icon :icon="faChevronRight" />
      </span>
    </router-link>

    <router-link
      class="navbar-item flex-mobile has-space-between is-hidden-touch"
      to="/contact-us"
      active-class="is-active"
      @click.stop="handleNavigationClick"
    >
      <span>Contact</span>
      <span class="icon match-line is-hidden-desktop">
        <Icon :icon="faChevronRight" />
      </span>
    </router-link>

    <router-link
      class="navbar-item flex-mobile has-space-between is-hidden-touch"
      to="/discover-print-savings"
      active-class="is-active"
      @click.stop="handleNavigationClick"
    >
      <span>Discover savings</span>
      <span class="icon match-line is-hidden-desktop">
        <Icon :icon="faSterlingSign" />
      </span>
    </router-link>

    <div class="navbar-dropdown is-right">
      <router-link
        class="navbar-item dropdown-item-icon flex-mobile has-space-between is-hidden-desktop"
        to="/customer-support"
        active-class="is-active"
        @click.stop="handleNavigationClick"
      >
        <span>Support</span>
        <span class="icon match-line"> <Icon :icon="faChevronRight" /></span>
      </router-link>
    </div>

    <div class="navbar-dropdown is-right">
      <router-link
        class="navbar-item dropdown-item-icon flex-mobile has-space-between is-hidden-desktop"
        to="/contact-us"
        active-class="is-active"
        @click.stop="handleNavigationClick"
      >
        <span>Contact</span>
        <span class="icon match-line"> <Icon :icon="faChevronRight" /></span>
      </router-link>
    </div>
    <div class="navbar-dropdown is-right">
      <router-link
        class="navbar-item dropdown-item-icon flex-mobile has-space-between is-hidden-desktop"
        to="/discover-print-savings"
        active-class="is-active"
        @click.stop="handleNavigationClick"
      >
        <span>Discover savings</span>
        <span class="icon match-line">
          <Icon :icon="faSterlingSign" />
        </span>
      </router-link>
    </div>
    <slot name="basket" />
  </div>
</template>
<script lang="ts" setup>
import { ref } from 'vue'
import { debounce } from 'lodash-es'

import { searchAncestors } from '@/plugins/util.js'

// Navigation / megamenu
import { Icon } from '@repo/ui'
import { faSterlingSign } from '@repo/icons/light/faSterlingSign'
import { faChevronRight } from '@repo/icons/light/faChevronRight'
import { faScannerImage } from '@repo/icons/light/faScannerImage'
import { faUserGroup } from '@repo/icons/light/faUserGroup'
import { faNetworkWired } from '@repo/icons/light/faNetworkWired'
import { faIndustry } from '@repo/icons/light/faIndustry'
import { faCreditCard } from '@repo/icons/light/faCreditCard'
import { faCubes } from '@repo/icons/light/faCubes'
import { faWrench } from '@repo/icons/light/faWrench'
import { faPuzzlePiece } from '@repo/icons/light/faPuzzlePiece'
import { faFile } from '@repo/icons/light/faFile'
import { faShield } from '@repo/icons/light/faShield'
import { faEye } from '@repo/icons/light/faEye'
import { faChartPie } from '@repo/icons/light/faChartPie'
import { faChartBar } from '@repo/icons/light/faChartBar'
import MegaMenu from './MegaMenu.vue'
// import NavDropdown from './NavDropdown.vue'

const leaveDropdown = debounce((cb) => {
  cb()
}, 40)

const emit = defineEmits<{
  'toggle-back': [void]
  'navigate': [MouseEvent]
}>()

const props = defineProps<{
  deviceNav: boolean
}>()

const openItems = ref<HTMLElement | null>(null)
const hovering = ref(false)

const equipment = {
  mfp: {
    heading: 'Multifunctionals',
    items: [
      {
        text: 'A3 Colour',
        url: '/printers/multifunctionals/a3-colour',
        flyoutIcon: 'full-colour',
        icon: 'full-colour',
      },
      {
        text: 'A3 Mono',
        url: '/printers/multifunctionals/a3-mono',
        flyoutIcon: 'mono',
        icon: 'mono',
      },
      {
        text: 'A4 Colour',
        url: '/printers/multifunctionals/a4-colour',
        flyoutIcon: 'full-colour',
        icon: 'full-colour',
      },
      {
        text: 'A4 Mono',
        url: '/printers/multifunctionals/a4-mono',
        flyoutIcon: 'mono',
        icon: 'mono',
      },
      {
        text: 'View all',
        url: '/printers/multifunctionals',
        flyoutIcon: 'mono',
        icon: faChevronRight,
      },
    ],
  },
  production: {
    heading: 'Production printers',
    items: [
      {
        text: 'Colour',
        url: '/printers/production/colour',
        flyoutIcon: 'full-colour',
        icon: 'full-colour',
      },
      {
        text: 'Mono',
        url: '/printers/production/mono',
        flyoutIcon: 'mono',
        icon: 'mono',
      },
      {
        text: 'View all',
        url: '/printers/production',
        flyoutIcon: 'mono',
        icon: faChevronRight,
      },
    ],
  },
  plotterPrinters: {
    heading: 'Plotters',
    items: [
      {
        text: 'Colour',
        url: '/printers/plotters/colour',
        flyoutIcon: 'full-colour',
        icon: 'full-colour',
      },
      {
        text: 'Mono',
        url: '/printers/plotters/mono',
        flyoutIcon: 'mono',
        icon: 'mono',
      },
      {
        text: 'View all',
        url: '/printers/plotters',
        flyoutIcon: 'mono',
        icon: faChevronRight,
      },
    ],
  },
  printOnly: {
    heading: 'Printers',
    items: [
      {
        text: 'A3 Colour',
        url: '/printers/print-only/a3-colour',
        flyoutIcon: 'full-colour',
        icon: 'full-colour',
      },
      {
        text: 'A3 Mono',
        url: '/printers/print-only/a3-mono',
        flyoutIcon: 'mono',
        icon: 'mono',
      },
      {
        text: 'A4 Colour',
        url: '/printers/print-only/a4-colour',
        flyoutIcon: 'full-colour',
        icon: 'full-colour',
      },
      {
        text: 'A4 Mono',
        url: '/printers/print-only/a4-mono',
        flyoutIcon: 'mono',
        icon: 'mono',
      },
      {
        text: 'View all',
        url: '/printers/print-only',
        flyoutIcon: 'mono',
        icon: faChevronRight,
      },
    ],
  },
  scanners: {
    heading: 'Scanners',
    items: [
      {
        text: 'Personal',
        url: '/scanners/personal',
        icon: faScannerImage,
      },
      {
        text: 'Workgroup',
        url: '/scanners/workgroup',
        icon: faUserGroup,
      },
      {
        text: 'Departmental',
        url: '/scanners/departmental',
        icon: faNetworkWired,
      },
      {
        text: 'Production',
        url: '/scanners/production',
        icon: faIndustry,
      },
      {
        text: 'View all',
        url: '/scanners',
        icon: faChevronRight,
      },
    ],
  },
  finishers: {
    heading: 'Finishers',
    items: [
      {
        text: 'View all',
        url: '/finishers',
        icon: faChevronRight,
      },
    ],
  },
}
const tools = [
  {
    text: 'Discover savings',
    url: '/discover-print-savings',
    icon: 'faSterlingSign',
  },
]

const services = {
  services: {
    heading: 'Services',
    items: [
      {
        text: 'Lease & purchase',
        url: '/services/leasing-and-purchase',
        icon: faCreditCard,
      },
      {
        text: 'Service agreements',
        url: '/services/service-agreements',
        icon: faCubes,
      },
      {
        text: 'Repairs & servicing',
        url: '/services/repairs-and-servicing',
        icon: faWrench,
      },
      {
        text: 'Integrations',
        url: '/services/integrations',
        icon: faPuzzlePiece,
      },
      {
        text: 'Document management',
        url: '/services/document-management',
        icon: faFile,
      },
      {
        text: 'Document security',
        url: '/services/document-security',
        icon: faShield,
      },
      {
        text: 'Remote care',
        url: '/services/remote-care',
        icon: faEye,
      },
      {
        text: 'Print audit',
        url: '/services/print-audit',
        icon: faChartPie,
      },
      {
        text: 'Print cost control',
        url: '/services/print-cost-control',
        icon: faChartBar,
      },
      {
        text: 'Papercut',
        url: '/services/papercut',
        icon: faFile,
      },
    ],
  },
}

type NavigationEvent = MouseEvent & {
  currentTarget: HTMLElement
  target: HTMLElement
}

function handleNavigationClick(e: NavigationEvent) {
  emit('toggle-back')
  emit('navigate', e)

  searchAncestors(
    e.target,
    'is-active',
    (el) => {
      el.classList.remove('is-active')
    },
    'nav-item'
  )
}
// Handle clicking of menu sub-item
function itemClicked(e: NavigationEvent) {
  // Only needed on device menus, as larger screens show all options at once
  if (!props.deviceNav) {
    return
  }
  // Add active class to element and push onto array tacking elements
  e.currentTarget.classList.add('is-active')
  // this.openItems.push(e.currentTarget)
  emit('toggle-back')
}

function handleHoverState(element: HTMLElement | null, className: string, shouldAdd: boolean) {
  // Handle hover state for the given element
  if (element) {
    element.classList.toggle(className, shouldAdd)
  }
}

function itemHoverOn(e: NavigationEvent) {
  // Start capturing mouse move events
  if (e.currentTarget === openItems.value) {
    leaveDropdown.cancel()
  } else {
    handleHoverState(e.target, 'is-active', true)
    openItems.value = e.currentTarget
  }
}
function itemHoverOff(e: NavigationEvent) {
  // When moving offhover item, wait a small amount of time before removing class, in case
  // we are just moving to dropdown
  leaveDropdown(() => {
    handleHoverState(e?.target ?? null, 'is-active', false)
    openItems.value = null
  })
}
</script>
